import React, { useEffect, useRef } from 'react';
import { SLUG } from './storeConfig';

/** Colour swatches + size chips for one product (see productOptions.getOptions).
 *  Sold-out choices stay visible but crossed out and unselectable, so a shopper can see
 *  their size exists rather than wondering whether it was ever made. missing: a count of
 *  add-to-bag presses with a choice still to make — each one brings that choice into view
 *  (on phones the buttons are pinned to the bottom, often far from the picker). */
export default function OptionPicker({ options, color, size, onColor, onSize, missing, idPrefix = 'opt' }) {
  const showColors = options.colors.length > 1;
  // Colour versions with their own photo are chosen by photo, like the brand's own site.
  const photoSwatches = options.colors.length > 0 && options.colors.every(c => c.image);
  const sizes = options.kind === 'combo' ? (color ? options.sizesFor(color) : []) : options.sizesFor();
  const colorLabel = options.colors.find(c => c.name === color)?.label;
  const needsColor = options.colors.length > 0 && !color;
  const needsSize = options.hasSize && !size;
  const sizeWord = options.kind === 'label' ? 'option' : 'size';
  const colorRef = useRef(null);
  const sizeRef = useRef(null);
  useEffect(() => {
    const group = missing && (needsColor ? colorRef.current : needsSize ? sizeRef.current : null);
    if (!group) return;
    group.scrollIntoView({ block: 'center', behavior: 'smooth' });
    group.querySelector('button:not([disabled])')?.focus({ preventScroll: true });
    // Only when the shopper presses add again — not when a pick clears the need.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missing]);

  return (
    <>
      {showColors && (
        <div className="rt-opt" ref={colorRef}>
          <div className="rt-opt-label" id={`${idPrefix}-color`}>Colour{colorLabel && <span>: {colorLabel}</span>}</div>
          <div className={`rt-swatches${photoSwatches ? ' rt-swatches--photo' : ''}`} role="group" aria-labelledby={`${idPrefix}-color`}>
            {options.colors.map(c => (
              <button key={c.name} type="button"
                className={`${photoSwatches ? 'rt-photo-swatch' : 'rt-swatch'}${c.available ? '' : ' rt-swatch--oos'}`}
                style={photoSwatches ? undefined : { background: c.hex }} title={c.available ? c.label : `${c.label} — sold out`}
                aria-label={c.available ? c.label : `${c.label}, sold out`} aria-pressed={color === c.name}
                disabled={!c.available} onClick={() => onColor(c.name)}>
                {photoSwatches && <img src={c.image} alt="" loading="lazy" />}
              </button>
            ))}
          </div>
          {missing > 0 && needsColor && <div className="rt-opt-hint" role="alert">Please choose a colour</div>}
        </div>
      )}
      {options.colors.length === 1 && (
        <div className="rt-opt rt-opt-label">Colour<span>: {options.colors[0].label}</span></div>
      )}

      {options.hasSize && (
        <div className="rt-opt" ref={sizeRef}>
          <div className="rt-opt-label" id={`${idPrefix}-size`}>
            {options.kind === 'label' ? 'Option' : 'Size'}{size && <span>: {size}</span>}
          </div>
          {options.kind === 'combo' && !color ? (
            <div className="rt-stock">Choose a colour to see sizes</div>
          ) : (
            <div className="rt-sizes" role="group" aria-labelledby={`${idPrefix}-size`}>
              {sizes.map(s => (
                <button key={s.value} type="button" className="rt-size" aria-pressed={size === s.value}
                  disabled={!s.available} aria-label={s.available ? s.value : `${s.value}, sold out`}
                  onClick={() => onSize(s.value)}>
                  {s.value}
                </button>
              ))}
            </div>
          )}
          {missing > 0 && needsSize && !needsColor && <div className="rt-opt-hint" role="alert">Please choose a {sizeWord}</div>}
        </div>
      )}
    </>
  );
}

/** Auto-picks any axis with exactly one in-stock choice — not a real choice, so never asked. */
export function autoPick(options) {
  const inStockColors = options.colors.filter(c => c.available);
  const color = inStockColors.length === 1 ? inStockColors[0].name : null;
  const sizes = (options.kind === 'combo' ? (color ? options.sizesFor(color) : []) : options.sizesFor()).filter(s => s.available);
  return { color, size: sizes.length === 1 ? sizes[0].value : null };
}

const LAST_SIZE_KEY = `${SLUG}_last_size`;

/** The size the shopper last chose by hand on this device — preselected on the next product
 *  that has it in stock. */
export function rememberSize(size) {
  try { localStorage.setItem(LAST_SIZE_KEY, size); } catch { /* storage blocked */ }
}
function lastSize() {
  try { return localStorage.getItem(LAST_SIZE_KEY); } catch { return null; }
}

/** What the product page and quick view open with: every choice made, so one tap adds to bag.
 *  Colour: the one whose own photo is the main photo, else the first in stock. A plain colour
 *  list (no photo per colour) can't say which colour its photos show, so its first colour may
 *  not be the one pictured. Size: defaultSize. Quick add on a card keeps autoPick — it adds
 *  without showing the choice, so it may only fill in what isn't a real choice. */
export function preselect(options, mainImage) {
  const colors = options.colors.filter(c => c.available);
  const color = (colors.find(c => mainImage && c.image === mainImage) || colors[0])?.name ?? null;
  return { color, size: defaultSize(options, color) };
}

/** The shopper's remembered size when this product (in this colour) has it in stock, else M,
 *  else the middle in-stock size (32 of 28–36). Non-size options ("500ML") start on the first
 *  in stock. */
export function defaultSize(options, color) {
  if (!options.hasSize) return null;
  const sizes = (options.kind === 'combo' ? (color ? options.sizesFor(color) : []) : options.sizesFor())
    .filter(s => s.available).map(s => s.value);
  if (!sizes.length) return null;
  if (options.kind === 'label') return sizes[0];
  const remembered = lastSize();
  if (remembered && sizes.includes(remembered)) return remembered;
  return sizes.find(s => s.toUpperCase() === 'M') || sizes[Math.floor((sizes.length - 1) / 2)];
}

/** True when every required axis has a pick. */
export const isComplete = (options, color, size) =>
  (options.colors.length === 0 || !!color) && (!options.hasSize || !!size);
